import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { get, uniqBy } from "lodash";

import Loading from "../../../../components/Loading/Loading";
import SelectSides from "../SelectSides/SelectSides";
import SwitchOnKitOptions from "../SwitchOnKitItemBuilder/SwitchOnKitOptions";
import WaterproofSolutionIncludedItems from "./WaterproofSolutionIncludedItems";
import WaterproofSolutionColours from "./WaterproofSolutionColours";
import { CartStatus, OrderBuilderStatus } from "../../store/constants";
import { OrderStatus } from "../../../storeHome/store/constants";

import(/* webpackChunkName: "order-builder-ite-custom-options" */ "../ITECustomOptions/iteCustomOptions.scss");
import(/* webpackChunkName: "order-builder-switch-on-kit" */ "../SwitchOnKitItemBuilder/switchOnKitItemBuilder.scss");
import(/* webpackChunkName: "order-builder-waterproof-solution" */ "./waterproofSolutionItemBuilder.scss");

const INCLUDED_CODES = ["AB_WATER_BATTERY", "AB_WATER_ORINGS", "AB_WATER_CLIP"];
const COLOUR_CODES = ["AB_CABLE_COLOUR", "AB_CAP_COLOUR"];
const CABLE_OPTION_CODES = ["AB_CABLE_LENGTH"];
const OTHER_OPTION_CODES = ["AB_MAGNET_TYPE", "AB_MAGNET_STRENGTH", "AB_WEARING_OPTION"];
const OPTION_CODES = [...CABLE_OPTION_CODES, ...OTHER_OPTION_CODES];
const REQUIRED_CODES = [...COLOUR_CODES, ...OPTION_CODES];
const splitOptions = (value) => (value || "").split("|").filter(Boolean);

const WaterproofSolutionItemBuilder = ({
  cartData, cartStatus, addOrderToCart, orderBuilderStatus, orderBuilderData,
  setIsStockOrder, loadCustomOptionsGroup, customOptionsGroupData,
  customOptionsGroupLeft, customOptionsGroupRight,
  loadConfigurationGroup, configurationGroupData,
  configurationGroupLeft, configurationGroupRight,
}) => {
  const history = useHistory();
  const items = get(orderBuilderData, "Items", []);
  const models = get(orderBuilderData, "Models", []);
  const [selectedModelCode, setSelectedModelCode] = useState("");
  const [leftOrRight, setLeftOrRight] = useState("");
  const [linked, setLinked] = useState(true);
  const [customOptions, setCustomOptions] = useState({ R: {}, L: {} });
  const [submittedItems, setSubmittedItems] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);

  const availableModels = useMemo(() => {
    const codes = items.map((item) => item.ModelTypeCode).filter(Boolean);
    return uniqBy(models.filter((model) => codes.includes(model.Code)), "Code");
  }, [items, models]);
  const selectedModelItems = useMemo(
    () => items.filter((item) => item.ModelTypeCode === selectedModelCode),
    [items, selectedModelCode]
  );
  const selectedSides = leftOrRight === "B" ? ["R", "L"] : leftOrRight ? [leftOrRight] : [];
  const rightSelected = selectedSides.includes("R");
  const leftSelected = selectedSides.includes("L");
  const getOptionsForSide = (side) =>
    (side === "R" ? customOptionsGroupRight : customOptionsGroupLeft) || [];
  const getSideItem = (side) => selectedModelItems.find((item) =>
    (item.Code || "").toUpperCase().endsWith(`-${side}`) ||
    (item.Name || "").toLowerCase().includes(side === "R" ? "right" : "left")
  );

  const setSideSelected = (side, selected) => {
    const otherSide = side === "R" ? leftSelected : rightSelected;
    if (selected && otherSide) setLeftOrRight("B");
    else if (selected) setLeftOrRight(side);
    else if (otherSide) setLeftOrRight(side === "R" ? "L" : "R");
    else setLeftOrRight("");
  };

  useEffect(() => {
    if (!selectedModelCode && availableModels.length) setSelectedModelCode(availableModels[0].Code);
  }, [availableModels, selectedModelCode]);

  useEffect(() => {
    if (selectedModelCode && loadCustomOptionsGroup) {
      loadCustomOptionsGroup("B", selectedModelCode, customOptionsGroupData || {});
      const model = availableModels.find((entry) => entry.Code === selectedModelCode);
      if (model && model.ConfigurationGroupCode && loadConfigurationGroup) {
        loadConfigurationGroup("B", model.ConfigurationGroupCode, configurationGroupData || {});
      }
      setCustomOptions({ R: {}, L: {} });
    }
  }, [selectedModelCode]);

  useEffect(() => {
    const populateFirstValues = (side, current) => {
      const next = { ...current };
      getOptionsForSide(side).filter((option) => [...INCLUDED_CODES, ...REQUIRED_CODES].includes(option.Code)).forEach((option) => {
        if (INCLUDED_CODES.includes(option.Code)) {
          return;
        }
        if (COLOUR_CODES.includes(option.Code)) {
          const configuration = side === "R" ? configurationGroupRight : configurationGroupLeft;
          const colours = option.Code === "AB_CABLE_COLOUR"
            ? get(configuration, "ShellColours", []) : get(configuration, "FaceplateColours", []);
          const order = splitOptions(option.Options).map((value) => value.split("-").pop().trim());
          const firstColour = order.find((code) => colours.some((colour) => colour.Code === code));
          if (!next[option.Name] && firstColour) next[option.Name] = firstColour;
          return;
        }
        const values = splitOptions(option.Options);
        if (!next[option.Name] && values.length) next[option.Name] = values[0];
      });
      return next;
    };
    if (!selectedModelCode || (!customOptionsGroupRight && !customOptionsGroupLeft)) return;
    setCustomOptions((current) => {
      const next = { R: populateFirstValues("R", current.R), L: populateFirstValues("L", current.L) };
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, [selectedModelCode, customOptionsGroupRight, customOptionsGroupLeft, configurationGroupRight, configurationGroupLeft, customOptions]);

  useEffect(() => {
    if (cartStatus === CartStatus.SUCCESS && submittedItems.length) {
      history.push({ pathname: `/related-products/${submittedItems[0].Code}`, state: { productsAddedToCart: submittedItems } });
    }
  }, [cartStatus, history, submittedItems]);

  const setOption = (side, option, value) => {
    setCustomOptions((current) => {
      const applyOption = (source) => {
        const next = { ...source, [option.Name]: value };
        if (option.Code === "AB_WATER_BATTERY") {
          const group = getOptionsForSide(side);
          ["AB_WATER_ORINGS", "AB_WATER_CLIP"].forEach((code) => {
            const included = group.find((entry) => entry.Code === code);
            if (included && value) next[included.Name] = "Yes";
          });
        }
        if (option.Code === "AB_MAGNET_TYPE") {
          const strength = getOptionsForSide(side).find((entry) => entry.Code === "AB_MAGNET_STRENGTH");
          if (strength) next[strength.Name] = splitOptions(strength.Options)[0] || "";
        }
        return next;
      };
      const nextSide = applyOption(current[side]);
      const otherSide = side === "R" ? "L" : "R";
      return linked && rightSelected && leftSelected
        ? { ...current, [side]: nextSide, [otherSide]: applyOption(current[otherSide]) }
        : { ...current, [side]: nextSide };
    });
  };

  const addToOrder = () => {
    const errors = [];
    if (!selectedModelCode) errors.push("Please select a Waterproof Solution model");
    if (!leftOrRight) errors.push("Please select at least one side");
    selectedSides.forEach((side) => getOptionsForSide(side)
      .filter((option) => REQUIRED_CODES.includes(option.Code))
      .forEach((option) => {
        if (!customOptions[side][option.Name]) errors.push(`Please select ${option.Name} for ${side === "R" ? "Right" : "Left"}`);
      }));
    const itemsForCart = selectedSides.map((side) => {
      const item = getSideItem(side);
      if (!item) {
        errors.push(`The Waterproof Solution does not have a ${side === "R" ? "Right" : "Left"} item configured`);
        return null;
      }
      return { ...item, ItemCode: item.Code, Quantity: 1, Side: side, Type: "Standard", CustomOptions: customOptions[side] };
    }).filter(Boolean);
    setErrorMessages(errors);
    if (errors.length) return;
    const currentCart = cartData ? [...cartData] : [];
    itemsForCart.forEach((item) => {
      const index = currentCart.findIndex((existing) => existing.Code === item.Code && existing.Side === item.Side);
      if (index >= 0) currentCart[index] = { ...currentCart[index], Quantity: Number(currentCart[index].Quantity || 0) + 1 };
      else currentCart.push(item);
    });
    setSubmittedItems(itemsForCart);
    setIsStockOrder(OrderStatus.REGULAR);
    addOrderToCart(currentCart);
  };

  return <div className="order-builder-main-div waterproof-solution-item-builder-main-div">
    {orderBuilderStatus === OrderBuilderStatus.LOADING && <Loading />}
    {orderBuilderStatus === OrderBuilderStatus.ERROR && <div className="alert alert-danger">The Waterproof Solution products could not be loaded. Please try again.</div>}
    {orderBuilderStatus === OrderBuilderStatus.SUCCESS && <div>
      <h1>Configure your Waterproof Solution</h1>
      <p>Select the required side or sides and configure the options.</p>
      {!availableModels.length ? <div className="alert alert-info">There are currently no Waterproof Solution models available to order.</div> : <div>
        <SelectSides rightSelected={rightSelected} setRightSelectedSides={(selected) => setSideSelected("R", selected)} leftSelected={leftSelected} setLeftSelected={(selected) => setSideSelected("L", selected)} />
        {selectedModelCode && selectedSides.length > 0 && <section className="waterproof-solution-options ite-custom-options-component-main-div order-builder-component">
          <WaterproofSolutionIncludedItems rightSelected={rightSelected} leftSelected={leftSelected} linked={linked} setLinked={setLinked} getOptionsForSide={getOptionsForSide} selectedOptions={customOptions} setOption={setOption} />
          <div className="waterproof-aquamic-heading"><h2>Slim Headpiece AquaMic</h2><p>Select one option in each category.</p></div>
          <SwitchOnKitOptions title="Choose a cable length" codes={CABLE_OPTION_CODES} rightSelected={rightSelected} leftSelected={leftSelected} linked={linked} setLinked={setLinked} getOptionsForSide={getOptionsForSide} optionVisible={() => true} allowedValues={(option) => splitOptions(option.Options)} selectedOptions={customOptions} setOption={setOption} />
          <WaterproofSolutionColours rightSelected={rightSelected} leftSelected={leftSelected} linked={linked} setLinked={setLinked} getOptionsForSide={getOptionsForSide} selectedOptions={customOptions} setOption={setOption} configurationGroupRight={configurationGroupRight} configurationGroupLeft={configurationGroupLeft} />
          <SwitchOnKitOptions title="Choose magnet and wearing options" codes={OTHER_OPTION_CODES} rightSelected={rightSelected} leftSelected={leftSelected} linked={linked} setLinked={setLinked} getOptionsForSide={getOptionsForSide} optionVisible={() => true} allowedValues={(option) => splitOptions(option.Options)} selectedOptions={customOptions} setOption={setOption} />
        </section>}
        {errorMessages.length > 0 && <Row className="order-builder-component-title"><Col xs={12} className="alert alert-danger"><p>Please resolve the following errors</p><ul>{errorMessages.map((message) => <li key={message}>{message}</li>)}</ul></Col></Row>}
        <div className="item-builder-add-to-cart-button order-builder-component"><div className="order-builder-component-title"><button className="btn float-right" type="button" disabled={cartStatus === CartStatus.LOADING} onClick={addToOrder}>{cartStatus === CartStatus.LOADING ? "Adding to order..." : "Add to order"}</button></div></div>
      </div>}
    </div>}
  </div>;
};

WaterproofSolutionItemBuilder.propTypes = {
  cartData: PropTypes.array, cartStatus: PropTypes.string, addOrderToCart: PropTypes.func,
  orderBuilderStatus: PropTypes.string, orderBuilderData: PropTypes.object,
  setIsStockOrder: PropTypes.func, loadCustomOptionsGroup: PropTypes.func,
  customOptionsGroupData: PropTypes.object, customOptionsGroupLeft: PropTypes.array,
  customOptionsGroupRight: PropTypes.array,
  loadConfigurationGroup: PropTypes.func, configurationGroupData: PropTypes.object,
  configurationGroupLeft: PropTypes.object, configurationGroupRight: PropTypes.object,
};

export default WaterproofSolutionItemBuilder;
